import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ViewportService } from '../../services/viewport.service';
import { UserService } from '../../services/user.service';
import { ConfigService } from '../../services/config.service';
import { RoutingService } from '../../services/routing.service';
import { SearchService } from '../../search/services/search.service';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';


interface QuickSearch {
	id: string;
	query: string;
	criteria: string;
	sort: string;
	type: number;
	view: string;
	displayName: string;
	enabled: boolean;
}


/**
 * App home component.
 */
@Component({
	selector: 'kv-home',
	templateUrl: './home.component.html',
	styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, OnDestroy {
	private userEventSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	isMobile: boolean = false;
	mapEnabled: boolean = false;
	dashboardEnabled: boolean = false;

	query: string = '';
	type: number = 16;
	view: string = 'G';

	quickSearch: QuickSearch[] = [];

	// Disclaimer
	htmlContent: string = '';
	showDisclaimer = false;

	constructor(
		private viewportService: ViewportService,
		private configService: ConfigService,
		private userService: UserService,
		private http: HttpClient,
		private routingService: RoutingService,
		private router: Router,
		private searchService: SearchService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnInit(): void {
		this.searchService.clearSearches(false, false);
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
				this.buildQuickSearch();
			}
			if (event === "userPrefsUpdate") {
				this.userPermissions = this.userService.getUserPermissionsMap();
				this.processUserPermissions();
				this.buildQuickSearch();
			}
		});

		this.mapEnabled = this.configService.getConfig()?.esri?.enabled ?? false;

		this.loadDisclaimerTemplate();

		this.processUserPermissions();
		this.buildQuickSearch();
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}
	}

	private processUserPermissions(): void {
		if (this.userService?.user?.properties?.awpropertyObjectList) {
			this.dashboardEnabled = this.userService.user.properties.awpropertyObjectList.some(
				(x: any) => x.name === "DASHBOARD_ID" && x.value
			);

			for (let property of this.userService.user.properties.awpropertyObjectList) {
				if (property.name == "QUERY_TYPE") {
					this.type = property.value;
				}

				if (property.name == 'RESULT_LIST_DISPLAY_TYPE') {
					switch (property.value) {
						case '1':
							this.view = "G";
							break;
						case '2':
							this.view = "L";
							break;
						case '3':
							this.view = "T";
							break;
						case '4':
							this.view = this.mapEnabled ? "M" : "G";
							break;
						default:
							this.view = "G";
					}
				}
			}
		}
	}

	private buildQuickSearch(): void {
		let quickSearchConfig = this.configService.getConfig()?.search.quickSearch || [];
		this.quickSearch = cloneDeep(quickSearchConfig);

		this.quickSearch.forEach((qs: QuickSearch) => {
			qs.query = qs.query || '*';
			qs.type = qs.type || this.type;
			qs.view = qs.view || this.view;
			if (qs.view === 'M') {
				if (qs.enabled) {
					qs.enabled = this.mapEnabled;
				}
			}
			if (qs.id === 'popular') {
				if (qs.enabled) {
					qs.enabled = this.configService.getConfig()?.viewer.docViewsEnabled;
				}
			}
			qs.displayName = this.LOCALIZATION[qs.displayName] || qs.displayName || 'Search';
		});
	}

	doQuickSearch(qsearch: QuickSearch): void {
		let params = {
			q: qsearch.query,
			v: qsearch.view,
			criteria: qsearch.criteria,
			sort: qsearch.sort,
			type: qsearch.type.toString()
		};

		if (qsearch.id === 'latest') {
			// Calculate 2 weeks ago
			let now = dayjs();
			let lastUpdate = dayjs().subtract(2, 'weeks');
			// End range at tomorrow
			now = now.add(1, 'day');

			params.criteria = "load_date=" + lastUpdate.startOf("day").unix() + "|" + now.startOf("day").unix() + "";
			params.sort = 'load_date:DESC';
		}
		
        this.routingService.updateSearchUrl(params, true, true);
	}

	search(notify: boolean = true): void {
		let query = this.query;
		if (!query) {
			query = "*";
		}
        this.routingService.updateSearchUrl({q: query, v:this.view,type:this.type.toString()}, true, true);
	}
	
	advancedSearch(): void {
		const url = `/s`;
		this.router.navigate([url], {});
	}	

	// --- Disclaimer
	private loadDisclaimerTemplate() {
		let templatePath = undefined;

		if (this.configService.getConfig()?.auth?.disclaimer) {
			let disclaimerAccepted = localStorage.getItem('kv_disclaimer') === 'accepted';
			if (!disclaimerAccepted) {
				templatePath = '/app/aw-ui-angular/assets/templates/disclaimer.txt';
				this.showDisclaimer = true;
			}
		}

		if (this.showDisclaimer && templatePath) {
			this.http.get(templatePath, { responseType: 'text' }).subscribe({
				next: (html) => this.htmlContent = html,
				error: (err) => console.error('Error loading disclaimer template:', err)
			});
		}
	}

	acceptDisclaimer(): void {
		localStorage.setItem("kv_disclaimer", "accepted");
		this.showDisclaimer = false;
		this.userService.broadcastEvent.emit('disclaimerAccepted');
	}

	// AFHRA, redirect from custom disclaimer page  
	redirectBySuffix(suffix: string): void {
		if (suffix && suffix.length > 0) {
			this.userService.goHome();
		} else {
			localStorage.setItem("kv_disclaimer", "accepted");
			this.userService.goHome();
		}
	}
}
